import React from 'react'
import {
    CircularProgress,
    IconButton,
    ListItem,
    Popover,
    PopoverOrigin,
    Tooltip,
    useTheme,
} from '@material-ui/core'

import GetAppIcon from '@material-ui/icons/GetApp'
import MailOutline from '@material-ui/icons/MailOutline'
import PictureAsPdf from '@material-ui/icons/PictureAsPdf'
import PublishIcon from '@material-ui/icons/Publish'
import SyncIcon from '@material-ui/icons/Sync'
import TableChart from '@material-ui/icons/TableChart'

import { Container } from '../Container'
import { typographyStyle } from '../../theme/typographyStyle'

/** MUI v4 `ListItem` types reject `component="div"` together with `button={boolean}`; runtime is correct. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ListItemRow = ListItem as any

/** Primary workbook / document type (left icon). */
export type ReportFileType = 'excel' | 'pdf'

/** How the report is delivered (right action icon). */
export type ReportDeliveryMethod = 'download' | 'email'

interface Props {
    open: boolean
    onClose: () => void
    anchorEl: HTMLElement | null
    anchorOrigin?: PopoverOrigin
    reportItems?: ItemProps[]
    setupItems?: ItemProps[]
    reportLoading?: boolean
    setupLoading?: boolean
    width?: string | number
}

interface ItemLink {
    onClick?: (e: React.MouseEvent<HTMLElement>) => void
    title: string
    disabled?: boolean
}

export interface ItemProps {
    title: string
    /** Second line only (e.g. PDF subtitle); keeps the main title on one line. */
    titleSubtitle?: string
    downloadLink?: ItemLink
    uploadLink?: ItemLink
    syncLink?: ItemLink
    lineAbove?: boolean
    /** Report file type — left icon (Excel vs PDF). */
    reportFileType?: ReportFileType
    /** Delivery method — right icon (download file vs email). Defaults to download. */
    reportDelivery?: ReportDeliveryMethod
}

const FORMAT_ICON_SIZE = 22

const ReportFileTypeIcon = ({ fileType }: { fileType?: ReportFileType }) => {
    if (!fileType) return null

    switch (fileType) {
        case 'excel':
            return (
                <Tooltip title="Excel report">
                    <TableChart
                        style={{
                            fontSize: FORMAT_ICON_SIZE,
                            color: '#217346',
                        }}
                    />
                </Tooltip>
            )
        case 'pdf':
            return (
                <Tooltip title="PDF report">
                    <PictureAsPdf
                        style={{
                            fontSize: FORMAT_ICON_SIZE,
                            color: '#D32F2F',
                        }}
                    />
                </Tooltip>
            )
        default:
            return null
    }
}

export const ScheduleReportPopOver = (props: Props) => {
    const {
        open,
        onClose,
        anchorEl,
        anchorOrigin,
        reportItems,
        setupItems,
        reportLoading,
        setupLoading,
        width,
    } = props

    const theme = useTheme()

    const ICON_COL = 42

    const Item = ({
        item,
        sectionLocked,
    }: {
        item: ItemProps
        sectionLocked: boolean
    }) => {
        const hasDownload = !!item.downloadLink
        const downloadBlocked = !!item.downloadLink?.disabled || sectionLocked
        const downloadRowClickable = hasDownload && !downloadBlocked
        const runDownload = (e: React.MouseEvent<HTMLElement>) => {
            if (downloadBlocked) return
            item.downloadLink?.onClick?.(e)
        }

        const stop = (fn?: (e: React.MouseEvent<HTMLElement>) => void) => (
            e: React.MouseEvent<HTMLElement>,
        ) => {
            e.stopPropagation()
            if (sectionLocked) return
            fn?.(e)
        }

        const delivery: ReportDeliveryMethod = item.reportDelivery ?? 'download'
        const deliveryTooltip =
            delivery === 'email' ? 'Send by email' : 'Download file'

        return (
            <ListItemRow
                component="div"
                button={downloadRowClickable}
                onClick={downloadRowClickable ? runDownload : undefined}
                disableGutters
                style={{
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    borderTop: item.lineAbove
                        ? `1px solid ${theme.palette.grey[400]}`
                        : undefined,
                    borderRadius: theme.shape.borderRadius,
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(0.5),
                    paddingTop: theme.spacing(0.75),
                    paddingBottom: theme.spacing(0.75),
                    opacity: sectionLocked ? 0.55 : 1,
                    transition: theme.transitions.create(
                        ['background-color', 'opacity'],
                        {
                            duration: theme.transitions.duration.shortest,
                        },
                    ),
                }}
            >
                <div
                    style={{
                        flex: 1,
                        minWidth: 0,
                        marginLeft: theme.spacing(1),
                    }}
                >
                    <div
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {item.title}
                    </div>
                    {item.titleSubtitle ? (
                        <div
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                                marginTop: 2,
                                lineHeight: 1.2,
                            }}
                        >
                            {item.titleSubtitle}
                        </div>
                    ) : null}
                </div>

                <div style={{ width: ICON_COL, flexShrink: 0 }}>
                    {item.syncLink ? (
                        <Tooltip title={item.syncLink.title}>
                            <span style={{ display: 'inline-flex' }}>
                                <IconButton
                                    size="small"
                                    onClick={stop(item.syncLink.onClick)}
                                    disabled={
                                        sectionLocked || item.syncLink.disabled
                                    }
                                >
                                    <SyncIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : null}
                </div>

                <div style={{ width: ICON_COL, flexShrink: 0 }}>
                    {item.uploadLink ? (
                        <Tooltip title={item.uploadLink.title}>
                            <span style={{ display: 'inline-flex' }}>
                                <IconButton
                                    size="small"
                                    onClick={stop(item.uploadLink.onClick)}
                                    disabled={
                                        sectionLocked ||
                                        item.uploadLink.disabled
                                    }
                                >
                                    <PublishIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : null}
                </div>

                <div
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        marginLeft: theme.spacing(0.5),
                    }}
                >
                    {item.downloadLink ? (
                        <>
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    marginRight: theme.spacing(0.25),
                                }}
                            >
                                <ReportFileTypeIcon
                                    fileType={item.reportFileType}
                                />
                            </span>
                            <Tooltip
                                title={
                                    sectionLocked
                                        ? 'Please wait…'
                                        : item.downloadLink.title ||
                                          deliveryTooltip
                                }
                            >
                                <span style={{ display: 'inline-flex' }}>
                                    <IconButton
                                        size="small"
                                        onClick={stop(runDownload)}
                                        disabled={downloadBlocked}
                                        aria-label={deliveryTooltip}
                                    >
                                        {delivery === 'email' ? (
                                            <MailOutline fontSize="small" />
                                        ) : (
                                            <GetAppIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    ) : null}
                </div>
            </ListItemRow>
        )
    }

    const popoverWidth = width ?? 400

    return (
        <Popover
            id="SCHEDULE-REPORT-POPOVER"
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={anchorOrigin}
        >
            <Container
                style={{
                    width: popoverWidth,
                    minWidth: popoverWidth,
                    maxWidth: 480,
                    backgroundColor: theme.palette.grey[100],
                    flexDirection: 'column',
                    padding: theme.spacing(1),
                }}
            >
                {reportItems && (
                    <>
                        <Container
                            style={{
                                marginBottom: theme.spacing(0.5),
                                paddingLeft: theme.spacing(1),
                                paddingRight: theme.spacing(1),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle1,
                                    ),
                                    fontWeight: 700,
                                }}
                            >
                                Reports
                            </span>

                            <div style={{ flex: 1 }} />

                            {reportLoading && <CircularProgress size={20} />}
                        </Container>

                        {reportItems.map((item, idx) => (
                            <Item
                                key={`REPORT_ITEM_${idx}`}
                                item={item}
                                sectionLocked={!!reportLoading}
                            />
                        ))}
                    </>
                )}

                {setupItems && (
                    <>
                        <Container
                            style={{
                                marginTop: reportItems ? theme.spacing(1) : 0,
                                marginBottom: theme.spacing(0.5),
                                paddingLeft: theme.spacing(1),
                                paddingRight: theme.spacing(1),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle1,
                                    ),
                                    fontWeight: 700,
                                }}
                            >
                                Setup
                            </span>

                            <div style={{ flex: 1 }} />

                            {setupLoading && <CircularProgress size={20} />}
                        </Container>

                        {setupItems.map((item, idx) => (
                            <Item
                                key={`SETUP_ITEM_${idx}`}
                                item={item}
                                sectionLocked={!!setupLoading}
                            />
                        ))}
                    </>
                )}

                {/* TODO: Turnco upload sheet pending further discussion */}
            </Container>
        </Popover>
    )
}
